import Input from 'components/molecules/Input';
import {
  ContextConfigContent,
  ContextConfigContentType,
  ContextConfigTypes,
  SurveyTemplateConfig as SurveyTemplateConfigType,
} from 'utils/gloo-types';
import Typography from 'components/atoms/Typography';
import { SelectOption, ToggleSelectOption } from 'utils/frontendTypes';
import { useState } from 'react';
import { MediaUploadResult } from 'components/organisms/CloudinaryMediaUploader';
import { MediaUploadDto } from 'utils/mms';
import { Configs } from 'utils/constants';
import { isObject } from 'utils/common';
import { getI18nResponse } from 'utils';
import { DayOfWeekOptions, ReminderDayOptions, RepeatFrequencyOptions } from 'utils/selectOptions';
import SelectKeywords from 'components/molecules/SelectKeywords';
import ToggleButton from 'components/molecules/ToggleButton';
import HelpPopover from 'components/molecules/HelpPopover';
import { MMSMediaInput } from 'components/molecules/MMSMediaInput';
import Select from 'components/molecules/Select';
import SurveyMetrics from './SurveyMetrics';
import SurveyQuestions from './SurveyQuestions';

interface SurveyTemplateConfigProps {
  template?: any;
  handleChange: (data: any) => void;
  handleForm: (data: any) => void;
}
function SurveyTemplateConfig({ template, handleChange, handleForm }: SurveyTemplateConfigProps) {
  const [mmsMedia, setMmsMedia] = useState<Partial<MediaUploadResult>>({});

  const surveyContentTypeData = {
    left: {
      label: 'Opt-in',
      value: ContextConfigContentType.SURVEY_OPT_IN,
      icon: 'flag',
    } as ToggleSelectOption,
    right: {
      label: 'Scheduled',
      value: ContextConfigContentType.SURVEY_SCHEDULED,
      icon: 'calendar',
    } as ToggleSelectOption,
  };

  const templateConfig = {
    type: ContextConfigTypes.metricsets,
    content: {
      ...template?.config?.content,
      type: template?.config?.content?.type || surveyContentTypeData.left.value,
    } as ContextConfigContent,
    groups: template?.config?.groups,
    optOutUsers: template?.config?.optOutUsers,
    repeat: template?.config?.repeat,
    title: template?.config?.title || '',
    users: template?.config?.users,
  } as SurveyTemplateConfigType;

  const [mediaUploadDto, setMediaUploadDto] = useState<MediaUploadDto>(
    templateConfig.content?.endingResponseMedia?.[0] && {
      info: {
        public_id: templateConfig.content?.endingResponseMedia?.[0].cloudinaryId,
        url: templateConfig.content.endingResponseMedia?.[0].url,
      },
    },
  );

  const updateConfigProperty = (key, value) => {
    handleChange((prevState) => {
      const config = prevState.config || {};
      config[key] = value;

      return {
        ...prevState,
        config,
      };
    });

    handleForm((prevState) => {
      const config = prevState.config || {};
      config[key] = value;

      return {
        ...prevState,
        config,
      };
    });
  };

  const updateContent = (content: Partial<ContextConfigContent>) => {
    updateConfigProperty('content', {
      ...templateConfig.content,
      ...content,
    });
  };

  return (
    <div className="pt-2">
      <ToggleButton
        callback={(selected: string) => {
          updateConfigProperty('content', {
            ...templateConfig.content,
            type: selected,
          });
        }}
        initialState={templateConfig.content?.type}
        left={surveyContentTypeData.left}
        right={surveyContentTypeData.right}
      />
      <div className="flex flex-col mt-4 mx-1">
        <Input
          className="border border-blueLightest rounded shadow"
          label={
            <Typography className="text-blackDark" variant="label">
              Title
            </Typography>
          }
          onChangeText={(t) => updateConfigProperty('title', t)}
          placeholder="Survey"
          value={templateConfig.title}
        />
        {templateConfig.content?.type === surveyContentTypeData.right.value && (
          <>
            <Select
              dataDurableId="surveyScheduleFieldsSelectId"
              options={RepeatFrequencyOptions}
              onChange={(option: SelectOption) =>
                updateConfigProperty('repeat', {
                  ...templateConfig.repeat,
                  freqeuncy: option?.value,
                  byDay: option?.value === 'weekly' ? templateConfig.repeat.byDay : [],
                })
              }
              className="border border-blueLightest rounded shadow"
              label={
                <Typography className="text-blackDark mt-5" variant="label">
                  How often would you like this to go out?
                </Typography>
              }
              value={
                templateConfig.repeat?.freqeuncy
                  ? RepeatFrequencyOptions.find((o) => o.value === templateConfig.repeat?.freqeuncy)
                  : undefined
              }
            />
            {templateConfig.repeat?.freqeuncy === 'weekly' && (
              <div className="flex items-baseline mt-5">
                <div className="flex-1 mr-6">
                  <Select
                    dataDurableId="surveyScheduleFieldsSelectId2"
                    isMulti
                    label={
                      <Typography className="text-blackDark" variant="label">
                        Day(s)
                      </Typography>
                    }
                    options={DayOfWeekOptions}
                    value={
                      (templateConfig.repeat?.byDay || [])[0]
                        ? DayOfWeekOptions.filter((o) => templateConfig.repeat?.byDay.includes(o.value))
                        : undefined
                    }
                    onChange={(option: SelectOption[]) =>
                      updateConfigProperty('repeat', {
                        ...templateConfig.repeat,
                        byDay: !option ? [] : option.map((o) => o.value),
                      })
                    }
                    className="border border-blueLightest rounded shadow"
                  />
                </div>
              </div>
            )}
            <Select
              dataDurableId="surveyScheduleFieldsSelectId3"
              label={
                <div className="flex items-baseline">
                  <Typography className="text-blackDark mt-5" variant="label">
                    Would you like to send a reminder?
                  </Typography>
                  <HelpPopover
                    inline
                    className="ml-1"
                    size={14}
                    content="This will send out a reminder to anyone that has not reported/answered by the time the reminder goes out. This will NOT send to people that have completed."
                  />
                </div>
              }
              options={ReminderDayOptions}
              isClearable
              value={
                templateConfig.repeat?.reminderAfterDays
                  ? ReminderDayOptions.find((o) => o.value === templateConfig.repeat?.reminderAfterDays)
                  : null
              }
              onChange={(option: SelectOption) =>
                updateConfigProperty('repeat', {
                  ...templateConfig.repeat,
                  reminderAfterDays: option?.value,
                })
              }
              className="border border-blueLightest rounded shadow"
            />
          </>
        )}
        {templateConfig.content?.type === surveyContentTypeData.left.value && (
          <div className="mt-5">
            <div className="flex items-center mb-2">
              <Typography className="text-blackDark pl-1" variant="label">
                Would you like people to be able to opt in with certain keywords?
              </Typography>
              <HelpPopover
                className="ml-1"
                inline
                size={14}
                content="People will be able to fill this out multiple times."
              />
            </div>
            <SelectKeywords
              dataDurableId="surveyCreateEditSelectId"
              onCreate={(k) =>
                updateConfigProperty('content', {
                  ...templateConfig.content,
                  keywords: [...(templateConfig.content?.keywords || []), k],
                })
              }
              onChange={(k) =>
                updateConfigProperty('content', {
                  ...templateConfig.content,
                  keywords: k,
                })
              }
              startingKeywords={templateConfig.content?.keywords?.map((k) => ({ text: k }))}
            />
          </div>
        )}
        <SurveyMetrics content={templateConfig.content} onUpdateContent={updateContent} />
        <SurveyQuestions content={templateConfig.content} onUpdateContent={updateContent} />
        <div className="flex flex-col mt-5">
          <div className="flex items-center mb-2">
            <Typography className="text-blackDark pl-1" variant="label">
              Ending Response
            </Typography>
            <HelpPopover
              className="ml-1"
              inline
              size={14}
              content="This will be shown when someone finishes all the questions."
            />
          </div>
          <MMSMediaInput
            characterLimit={Configs.Sms.CharCount2}
            value={
              isObject(templateConfig.content?.endingResponse)
                ? getI18nResponse(templateConfig.content?.endingResponse)
                : templateConfig.content?.endingResponse || ''
            }
            onChangeText={(text) => updateContent({ ...templateConfig.content, endingResponse: text })}
            onShortenUrl={(text) => updateContent({ ...templateConfig.content, endingResponse: text })}
            onAddUserData={(userData) => {
              updateContent({
                ...templateConfig.content,
                endingResponse: templateConfig.content.endingResponse + userData,
              });
            }}
            cloudinaryImage={mmsMedia}
            style={{ minHeight: 125 }}
            placeholder="Thanks for participating, you're all set!"
            onMediaUpload={(mediaRef: MediaUploadResult) => {
              setMmsMedia(mediaRef);
            }}
            removeImageFromLocalState={() => {
              setMediaUploadDto({});
              setMmsMedia({});
            }}
            mediaUploadDto={mediaUploadDto}
          />
        </div>
      </div>
    </div>
  );
}

export default SurveyTemplateConfig;
